import type { PlanCode } from "./apiContracts";
import type { PrototypeApiClient } from "./apiClient";
import type { Root } from "./prototypeTypes";
import { completeMediaPlayerSeek, mountedFor, playMediaPlayer, previewMediaPlayerSeek } from "./mediaPlayerCore";
import { moveVideoChapter } from "./mediaPlayerChapterPlayback";
import { runtimeOf } from "./mediaPlayerProgress";
import { playerSelectors } from "./mediaPlayerSelectors";
import { beginMediaUserIntent, isMediaUserIntentCurrent } from './audioHandoffController';
import type { MountedPlayer } from './mediaPlayerTypes';

let vfCommandAbort: AbortController | null = null;

type VideoProgressActions = {
  preview: typeof previewMediaPlayerSeek;
  complete: typeof completeMediaPlayerSeek;
  play: typeof playMediaPlayer;
	/** Test seam; production derives this from the mounted video element. */
	isPlaying?: (root: Root) => boolean;
	mediaFor?: (root: Root) => MountedPlayer | undefined;
};

const defaultVideoProgressActions: VideoProgressActions = {
  preview: previewMediaPlayerSeek,
  complete: completeMediaPlayerSeek,
  play: playMediaPlayer,
};

export function stopVideoControls(): void {
  vfCommandAbort?.abort();
  vfCommandAbort = null;
}

export function wireVideoCommands(root: Root, api: PrototypeApiClient, topicId: string, chapterIndex: number, plan: PlanCode): void {
  vfCommandAbort?.abort();
  vfCommandAbort = typeof AbortController !== "undefined" ? new AbortController() : null;
  const options = vfCommandAbort ? ({ signal: vfCommandAbort.signal } as AddEventListenerOptions) : undefined;
  const target = root as Document | HTMLElement;
  const currentTarget = () => {
    const rt = runtimeOf(root);
    return {
      topicId: rt?.activeCourseId || topicId,
      chapterIndex: Number.isFinite(rt?.activeChapterIndex) && (rt?.activeChapterIndex ?? 0) > 0 ? rt!.activeChapterIndex! : chapterIndex,
    };
  };
  target.addEventListener("deepmox:video-next", (() => {
    const current = currentTarget();
    moveVideo(root, api, current.topicId, current.chapterIndex, plan, 1);
  }) as EventListener, options);
  target.addEventListener("deepmox:video-prev", (() => {
    const current = currentTarget();
    moveVideo(root, api, current.topicId, current.chapterIndex, plan, -1);
  }) as EventListener, options);
}

export function wireVideoProgress(root: Root, actions: VideoProgressActions = defaultVideoProgressActions): void {
  const doc = root instanceof Document ? root : root.ownerDocument ?? document;
  const progress = doc.getElementById(playerSelectors.video.progress);
  if (!progress) return;
  progress.onpointerdown = (ev) => {
    ev.preventDefault();
    hideReplayState(doc);
    progress.setPointerCapture?.(ev.pointerId);
	const mediaAtStart = actions.mediaFor?.(root) ?? mountedFor(root, 'video');
	const sourceGeneration = mediaAtStart?.sourceGeneration;
	const intent = beginMediaUserIntent(root);
    const wasPlaying = actions.isPlaying?.(root) ?? Boolean(mediaAtStart && !mediaAtStart.element.paused);
    let lastFraction = 0;
    let finished = false;
    const fraction = (event: PointerEvent) => {
      const rect = progress.getBoundingClientRect();
      return rect.width > 0 ? (event.clientX - rect.left) / rect.width : 0;
    };
    const apply = (event: PointerEvent) => {
      lastFraction = fraction(event);
      actions.preview(root, 'video', lastFraction);
    };
    const clearPointerHandlers = () => {
      progress.onpointermove = null;
      progress.onpointerup = null;
      progress.onpointercancel = null;
      progress.onlostpointercapture = null;
    };
    const resumeAfterSeek = () => {
      if (!wasPlaying) return;
		if (!mediaAtStart) return;
      let resumed = false;
      let timeoutId = 0;
      const resume = () => {
        if (resumed) return;
        resumed = true;
        clearTimeout(timeoutId);
		mediaAtStart.element.removeEventListener('seeked', resume);
		mediaAtStart.element.removeEventListener('canplay', resume);
		if (!isMediaUserIntentCurrent(root, intent)
			|| (actions.mediaFor?.(root) ?? mountedFor(root, 'video')) !== mediaAtStart
			|| mediaAtStart.sourceGeneration !== sourceGeneration) return;
        void actions.play(root, 'video');
      };
		mediaAtStart.element.addEventListener('seeked', resume, { once: true });
		mediaAtStart.element.addEventListener('canplay', resume, { once: true });
      timeoutId = window.setTimeout(resume, 600);
    };
    const finish = (event?: PointerEvent) => {
      if (finished) return;
      finished = true;
      if (event) apply(event);
      const completed = actions.complete(root, 'video', lastFraction);
      if (!completed) resumeAfterSeek();
      try {
        if (progress.hasPointerCapture?.(ev.pointerId)) progress.releasePointerCapture?.(ev.pointerId);
      } catch {
        // Losing capture is expected when the browser/background takes over.
      }
      clearPointerHandlers();
    };
    apply(ev);
    progress.onpointermove = apply;
    progress.onpointerup = finish;
    progress.onpointercancel = () => finish();
    progress.onlostpointercapture = () => finish();
  };
}

function hideReplayState(doc: Document): void {
  doc.getElementById(playerSelectors.video.replay)?.classList.remove("active");
  doc.getElementById(playerSelectors.video.container)?.classList.remove("replay-active");
}

export function moveVideo(root: Root, api: PrototypeApiClient, topicId: string, chapterIndex: number, plan: PlanCode, dir: 1 | -1) {
  moveVideoChapter(root, topicId, chapterIndex, plan, api, dir);
}
